import { Story } from '../../types/story';

export const viennaDivorce: Story = {
  id: 'vienna-divorce',
  title: 'Second Act',
  excerpt: 'A newly divorced woman in Vienna discovers that forty is just the beginning, especially when a younger man shows her what she\'s been missing.',
  city: 'Vienna',
  publishDate: '2025-12-08',
  readTime: '26 min read',
  tags: ['♀♂', 'age gap', 'divorced', 'confident'],
  categories: ['forbidden', 'intense', 'romantic'],
  intensity: 3,
  content: `The divorce papers had been finalized for exactly three months when I booked the trip to Vienna. Three months of living in the house that was now just mine, sleeping in a bed that felt too big, learning how to be alone after twenty years of marriage.

My friends said I was brave. My mother said I was foolish. My ex-husband said I was having a midlife crisis.

Maybe they were all right.

Vienna in October was beautiful—cool and crisp, the leaves turning gold in the parks, the coffee houses warm and inviting. I'd always wanted to visit, but Mark had never been interested. "Too old-fashioned," he'd said. "Too boring."

Mark was an idiot.

I was staying in a small hotel in the first district, close to everything. The plan was simple: museums during the day, opera or concerts at night, good food and wine whenever possible. A week of doing exactly what I wanted, when I wanted, without having to consider anyone else.

It was terrifying and exhilarating in equal measure.

The art history museum was overwhelming in the best way—room after room of masterpieces, more than I could possibly absorb in one visit. I was standing in front of a Klimt, completely lost in the gold and patterns, when I heard someone speak behind me.

"The Kiss is more famous, but I think this one is better. More honest."

I turned. He was young—mid-twenties, maybe—with dark hair and the kind of bone structure that belonged in a painting himself. Student, I guessed from the worn backpack and the notebook in his hand.

"You think so?" I asked, turning back to the painting. "Why?"

"The Kiss is about the moment of passion, the fantasy. But this one—" he gestured to the painting, "—this is about what comes after. The complexity. The reality."

"You sound like an art student."

"Guilty. Third year. I'm supposed to be sketching, but I keep getting distracted by the actual art." He smiled, and it was warm and genuine. "I'm Leo."

"Catherine." I didn't offer my hand, suddenly self-conscious about the age difference. I was forty-two. He was young enough to be... well, not my son, but close.

"American?"

"British. London. You're local?"

"Born and raised. Though I spent some years in Berlin." He looked at me with open curiosity. "First time in Vienna?"

"Yes. I'm here for a week. Doing the tourist thing."

"Alone?"

The question was casual, but I felt it land differently. "Yes. Alone."

"Then you need a proper guide. The museums are wonderful, but you're missing the real Vienna. The places locals go."

I should have said no. Should have made some excuse about having plans, about preferring to explore on my own. But I was tired of should, tired of being sensible.

"Alright," I said. "Show me."

He took me to a coffee house in the seventh district, the kind of place that hadn't changed in a hundred years. We sat at a small marble table and he ordered for us in rapid German—coffee and cake, the traditional way.

"So," he said, stirring sugar into his coffee. "What brings you to Vienna? Besides the art."

"Honestly? I'm celebrating my divorce."

His eyebrows rose. "That's... not what I expected."

"Sorry. Too much information?"

"No, no. I'm just surprised. You don't seem..." he trailed off.

"Bitter? Angry? Falling apart?"

"I was going to say sad."

I considered this. "I'm not, actually. The marriage had been over for years. We were just too comfortable, too scared to admit it. The divorce was a relief."

"How long were you married?"

"Twenty years. Since I was twenty-two."

He did the math in his head, and I saw the moment he realized how old I was. Waited for him to make an excuse, to suddenly remember somewhere he needed to be.

Instead, he leaned forward. "Twenty years. That's longer than I've been an adult. I can't even imagine."

"It's strange," I admitted. "I don't know who I am without it. Without him. I've been someone's wife for my entire adult life. Now I'm just... me. And I'm not sure who that is."

"So you came to Vienna to find out?"

"Something like that."

We talked for hours. He told me about his studies, about his dreams of becoming a curator, about growing up in Vienna and taking it for granted until he left and came back. I told him about my work as a solicitor, about my daughter who was away at university, about the strange freedom of starting over at forty-two.

"You know what I think?" he said, as we walked along the Ringstrasse in the fading afternoon light. "I think forty-two is the perfect age."

I laughed. "You're twenty-five. Everything seems old to you."

"I'm twenty-six, actually. And I'm serious. You're old enough to know who you are, what you want. Young enough to still do something about it. That's powerful."

We stopped at a wine bar in the first district, a modern place with natural wines and small plates. The conversation flowed as easily as the wine, and I found myself relaxing in a way I hadn't in years. He was easy to talk to, intelligent and curious, and he looked at me like I was interesting, like I mattered.

"Can I ask you something?" he said, after our second glass.

"Of course."

"Why did you say yes? To me showing you around. You could have said no."

"I don't know. Maybe because you asked. Maybe because I'm trying to say yes to things instead of always being careful."

"And how's that working out?"

I looked at him—really looked at him. The way the candlelight caught his features, the way he was leaning toward me, the way his eyes hadn't left mine all evening.

"Ask me tomorrow," I said quietly.

He paid the bill—insisted on it, despite my protests—and we walked back toward my hotel. The streets were quiet, the city settling into evening. We stopped outside the entrance, and I knew I should say goodnight, should go inside alone.

"Would you like to come up?" I heard myself ask. "For a drink. The hotel has a good bar."

"I'd like that."

The bar was small and dimly lit, mostly empty. We sat at a corner table and ordered whisky. The conversation had shifted, become charged with something unspoken.

"Catherine," he said finally. "I should tell you something."

"What?"

"I didn't run into you by accident at the museum. I saw you earlier, in the Egyptian collection. I followed you to the Klimt room because I wanted to talk to you."

"Why?"

"Because you were beautiful. Because you looked like you were really seeing the art, not just taking photos for Instagram. Because I wanted to know you."

My heart was beating too fast. "Leo, I'm seventeen years older than you."

"I know."

"This is crazy."

"Probably."

"I don't do things like this."

"Maybe you should start."

I kissed him. Or he kissed me. It didn't matter—what mattered was the way he tasted like whisky and youth, the way his hand came up to cup my face, the way everything else fell away.

"Come upstairs," I whispered. "To my room."

We barely made it through the door before we were kissing again, urgent and desperate. He pressed me against the wall, his hands in my hair, and I felt something wake up inside me that had been dormant for years.

"Are you sure?" he asked, pulling back to look at me.

"Yes. God, yes."

He undressed me slowly, reverently, like I was something precious. I was self-conscious at first—my body wasn't what it had been at twenty-five, wasn't what his probably was. But the way he looked at me, touched me, made me feel beautiful in a way I hadn't in years.

"You're stunning," he breathed, and I believed him.

I undressed him too, discovering his body—lean and strong, all smooth skin and hard muscle. We moved to the bed and he kissed his way down my body, taking his time, building anticipation until I was trembling.

"Tell me what you like," he said, his breath hot against my skin.

"I don't know anymore. It's been so long since anyone asked."

"Then let me find out."

He did. He explored my body with a patience and attention that made me feel worshipped. When his mouth finally found where I needed it most, I gasped, my hands fisting in his hair. He was skilled and attentive, and when I came, I cried out loud enough that I briefly worried about the neighboring rooms.

He kissed his way back up my body and I pulled him close, wanting him inside me. "Do you have—"

"Yes." He reached for his jeans, pulled out a condom.

When he entered me, I felt something shift—not just physical pleasure, though there was plenty of that, but something deeper. A reclaiming of my body, my desire, my right to pleasure. For twenty years, sex had been routine, perfunctory, something to get through. This was different. This was alive.

We moved together, finding a rhythm, and he was attentive and generous, making sure I was with him every step of the way. When I came again, he followed, and we collapsed together, breathless and laughing.

"Wow," he said, after we'd caught our breath.

"Yeah."

We lay tangled together, and I waited for the regret to hit, for the voice in my head that would tell me I'd made a mistake. But it didn't come. Instead, I felt light, free, more myself than I'd been in years.

"Stay," I said quietly. "Tonight. Stay with me."

He did. We made love again in the early morning, slower this time, and then he had to leave for a class. But he came back that evening, and the next, and the next. We spent the week together—museums and coffee houses and long walks through the city, and every night in my hotel room, discovering each other.

On my last day, we had breakfast at a café near the Naschmarkt.

"Come back," he said. "To Vienna. To me."

"Leo..."

"I know. You have a life in London. A career. A daughter. I'm not asking you to give that up. I'm just asking you to come back. Visit. See where this goes."

I thought about it. About the impossibility of it, the age difference, the distance. About all the reasons it wouldn't work.

And then I thought about how I felt when I was with him. Alive. Desired. Myself.

"Okay," I said. "I'll come back."

And I did. Once a month at first, then more often. We video called in between, sent long emails, built something that defied logic but felt true.

It's been a year now. I'm still in London, still working, still being a mother. But I'm also more. I'm a woman who takes what she wants, who isn't afraid of looking foolish, who knows that forty-two isn't the end of anything—it's just the beginning.

Leo and I are still together. We don't know what the future holds, don't have it all figured out. But we're happy, and that's enough.

Because sometimes the best thing you can do is stop worrying about what makes sense and start paying attention to what makes you feel alive. Sometimes the most radical act is choosing joy over judgment, pleasure over propriety, possibility over playing it safe.

And sometimes, forty-two is exactly the right age to start over.`
};
